import distance from 'euclidean-distance';

export enum ColorMapColor {
    Purple = 'purple',
    Pink = 'pink',
    Yellow = 'yellow',
    Red = 'red',
    Green = 'green',
    Black = 'black',
    White = 'white',
    Blue = 'blue',
    Gray = 'gray',
    Orange = 'orange',
    Brown = 'brown',
}

const candidates: { [key in ColorMapColor]: ColorMapValues } = {
    [ColorMapColor.Purple]: {
        feminineMasculine: -2,
        playfulSerious: -1,
        luxuriousAffordable: -2,
        modernClassic: 1,
        youthfulMature: 0,
        loudSubdued: 1,
    },
    [ColorMapColor.Pink]: {
        feminineMasculine: -2,
        playfulSerious: -1,
        luxuriousAffordable: -1,
        modernClassic: 1,
        youthfulMature: -1,
        loudSubdued: 0,
    },
    [ColorMapColor.Yellow]: {
        feminineMasculine: -1,
        playfulSerious: -1,
        luxuriousAffordable: 2,
        modernClassic: 1,
        youthfulMature: -2,
        loudSubdued: 1,
    },
    [ColorMapColor.Red]: {
        feminineMasculine: 0,
        playfulSerious: -2,
        luxuriousAffordable: 0,
        modernClassic: 1,
        youthfulMature: -2,
        loudSubdued: 2,
    },
    [ColorMapColor.Green]: {
        feminineMasculine: 0,
        playfulSerious: 0,
        luxuriousAffordable: 0,
        modernClassic: 0,
        youthfulMature: 0,
        loudSubdued: 0,
    },
    [ColorMapColor.Black]: {
        feminineMasculine: 0,
        playfulSerious: 0,
        luxuriousAffordable: -2,
        modernClassic: 2,
        youthfulMature: 0,
        loudSubdued: 0,
    },
    [ColorMapColor.White]: {
        feminineMasculine: 0,
        playfulSerious: 0,
        luxuriousAffordable: 1,
        modernClassic: 0,
        youthfulMature: -1,
        loudSubdued: 0,
    },
    [ColorMapColor.Blue]: {
        feminineMasculine: 0,
        playfulSerious: 1,
        luxuriousAffordable: 0,
        modernClassic: -1,
        youthfulMature: 1,
        loudSubdued: 0,
    },
    [ColorMapColor.Gray]: {
        feminineMasculine: 0,
        playfulSerious: 2,
        luxuriousAffordable: 0,
        modernClassic: -1,
        youthfulMature: 2,
        loudSubdued: -2,
    },
    [ColorMapColor.Orange]: {
        feminineMasculine: 1,
        playfulSerious: -2,
        luxuriousAffordable: 1,
        modernClassic: 1,
        youthfulMature: -1,
        loudSubdued: 2,
    },
    [ColorMapColor.Brown]: {
        feminineMasculine: 2,
        playfulSerious: 1,
        luxuriousAffordable: 0,
        modernClassic: 0,
        youthfulMature: 0,
        loudSubdued: -1,
    },
};

export interface ColorMapValues {
    [index: string]: number;
    feminineMasculine: number;
    playfulSerious: number;
    luxuriousAffordable: number;
    modernClassic: number;
    youthfulMature: number;
    loudSubdued: number;
}

type ColorNumberValues = { [key in ColorMapColor]: number };
type ColorStringValues = { [key in ColorMapColor]: string };

export default class ColorMap {
    getResult(sliderValues: ColorMapValues): ColorNumberValues {
        const nearestNeighbours = (distance: ColorMapValues) => [
            distance.feminineMasculine,
            distance.playfulSerious,
            distance.luxuriousAffordable,
            distance.modernClassic,
            distance.youthfulMature,
            distance.loudSubdued,
        ];

        return Object.keys(candidates).reduce((o: ColorNumberValues, c: ColorMapColor) => {
            o[c] = distance(nearestNeighbours(sliderValues), nearestNeighbours(candidates[c]));
            return o;
        }, this.initialColorNumberValues());
    }

    private initialColorNumberValues(): ColorNumberValues {
        const initial: Partial<ColorNumberValues> = {};
        for (const color in ColorMapColor) {
            initial[ColorMapColor[color] as ColorMapColor] = 0;
        }
        return initial as ColorNumberValues;
    }

    getColor(sliderValues: ColorMapValues): ColorMapColor {
        const results = this.getResult(sliderValues);

        let maxValue = null;
        let bestColor = ColorMapColor.Green;

        for (const untypedColor in results) {
            const color = untypedColor as ColorMapColor;
            if (maxValue == null || results[color] < maxValue) {
                maxValue = results[color];
                bestColor = color;
            }
        }

        return bestColor;
    }

    getColorHexValue(color: ColorMapColor) {
        const hexValues: ColorStringValues = {
            [ColorMapColor.Purple]: '#D33479',
            [ColorMapColor.Pink]: '#D33479',
            [ColorMapColor.Yellow]: '#FEE715',
            [ColorMapColor.Red]: '#EB3C27',
            [ColorMapColor.Green]: '#9FC131',
            [ColorMapColor.Black]: '#000000',
            [ColorMapColor.White]: '#FFFFFF',
            [ColorMapColor.Blue]: '#008DB9',
            [ColorMapColor.Gray]: '#727272',
            [ColorMapColor.Orange]: '#FB8B23',
            [ColorMapColor.Brown]: '#87462E',
        };

        return hexValues[color];
    }
}
